import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useEmails, Email } from '../../emails/useData';
import { emailDetailPath } from '../../emails/utils/emailPartitionAtQuery';
import { formatDateLocalized } from '../../../config/appConfig';
import { useTheme } from '../../../contexts/ThemeContext';
import { listRowClass } from '../themeHelpers';
import GenericListWidget from './GenericListWidget';

export interface RecentEmailsProps {
  limit?: number;
  embedded?: boolean;
  settings?: Record<string, unknown>;
  isEditing?: boolean;
}

const RecentEmails: React.FC<RecentEmailsProps> = ({
  limit = 5,
  embedded,
  settings,
}) => {
  const effectiveLimit = (settings?.limit as number) ?? limit;
  const { data: emails = [], isLoading } = useEmails(undefined, { skip: 0, take: 50 });
  const arr = Array.isArray(emails) ? emails : [];

  const items = React.useMemo(() => {
    return [...arr]
      .sort((a: Email, b: Email) => {
        const ta = new Date(a.date || a.receivedDate || 0).getTime();
        const tb = new Date(b.date || b.receivedDate || 0).getTime();
        return tb - ta;
      })
      .slice(0, effectiveLimit);
  }, [arr, effectiveLimit]);

  const { colors } = useTheme();
  const navigate = useNavigate();

  const fromDisplay = (e: Email) => {
    const from = e.from?.[0];
    return from?.name || from?.email || 'Unknown';
  };

  return (
    <GenericListWidget<Email>
      title="Recent Emails"
      viewAllPath="/emails"
      data={items}
      isLoading={isLoading}
      limit={effectiveLimit}
      emptyMessage="No recent emails"
      getItemKey={(e) => (e.partitionAt ? `${e.id}-${e.partitionAt}` : e.id)}
      embedded={embedded}
      renderItem={(email) => (
        <div
          role="button"
          tabIndex={0}
          onClick={() => navigate(emailDetailPath(email.id, email.partitionAt))}
          onKeyDown={(e) =>
            e.key === 'Enter' && navigate(emailDetailPath(email.id, email.partitionAt))
          }
          className={listRowClass(colors, !email.isRead ? 'font-medium' : undefined)}
        >
          <div className="flex-1 min-w-0">
            <p className={`text-xs sm:text-sm ${colors.text} line-clamp-1`}>
              {email.subject || '(No subject)'}
            </p>
            <p className={`text-[10px] sm:text-xs ${colors.textMuted} mt-1`}>
              {fromDisplay(email)}
              {email.date &&
                ` · ${formatDateLocalized(email.date, { month: 'short', day: 'numeric' })}`}
            </p>
          </div>
        </div>
      )}
    />
  );
};

export default RecentEmails;
